import { useCallback, useEffect, useRef, useState } from "react";
import {
  cancelReservation,
  createWalkIn,
  getTableReservation,
  getTableStatus,
  isGuestRisk,
  markCompleted,
  markNoShow,
  markSeated,
  reassignTable,
  revertNoShow,
  setFocusTableIds as setFocusTableIdsAction,
  swapTables,
  useAppStore,
  useLiveReservations,
} from "@/store/store";
import { useGuests } from "@/hooks/use-guests";
import { WalkinOverbookConfirm } from "@/components/WalkinOverbookConfirm";
import { usePlanWalkin, useSuggestTables } from "@/hooks/use-reservations";
import { useShallow } from "zustand/shallow";
import type { Reservation, TableStatus as StoreTableStatus, Table } from "@/store/types";
import { MapEngine } from "@/lib/map-engine";
import type { TableStatus } from "@/lib/map-engine";
import { WORLD_BOUNDS, buildLayout } from "@/lib/map-adapter";
import { reservationTagLabel } from "@/lib/reservation-tags";
import {
  dayjs,
  formatRemainingTime,
  minutesRemaining,
  minutesUntilNoShow,
  parseInTz,
  todayStr,
} from "@/utils/date";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Badge } from "@/components/ui/badge";
import { Spinner } from "@/components/ui/spinner";
import { sileo } from "sileo";
import {
  AlertDialog,
  AlertDialogAction,
  AlertDialogCancel,
  AlertDialogContent,
  AlertDialogDescription,
  AlertDialogFooter,
  AlertDialogHeader,
  AlertDialogTitle,
} from "@/components/ui/alert-dialog";
import {
  MapToolbar,
  MapToolbarButton,
  MapToolbarDivider,
  MapToolbarLockToggle,
  MapToolbarModeToggle,
} from "@/components/map/MapToolbar";
import { loadCamera, saveCamera } from "@/lib/map-camera";
import { useAuthStore } from "@/store/auth-store";
import { useTables } from "@/hooks/use-tables";
import { useTableGroups } from "@/hooks/use-table-groups";
import { useZones } from "@/hooks/use-zones";
import { useActiveLocationTimezone, useLocationSettings } from "@/hooks/use-locations";
import { findConnectedGroup } from "@/lib/table-graph";
import { useMediaQuery } from "@/hooks/use-media-query";
import { useOrganizationSettings } from "@/hooks/use-organizations";
import { ReservationContextMenu, performMidServiceMove } from "@/components/ReservationContextMenu";
import { TableExplorer, type TableExplorerPick } from "@/components/TableExplorer";
import { apiApplyPlan, apiSeatReservation } from "@/lib/reservation-api";
import {
  AlertTriangle,
  Check,
  ChevronRight,
  Clock,
  Globe,
  Lock,
  MapPin,
  Minus,
  Plus,
  Shrink,
  UserPlus,
  Users,
  X,
} from "lucide-react";
import { confirmReservationTables } from "@/store/walk-in-zone-confirm-store";

// Compact icon for the reservation card showing how committed the
// engine should treat this booking's table assignment. ``none`` is
// the default state and is intentionally rendered as nothing — the
// floor would be noisy if every card carried a "flexible" badge.
function PinBadge({ level }: { level: "none" | "zone" | "table" }) {
  if (level === "none") return null;
  if (level === "zone") {
    return (
      <span
        className="inline-flex items-center gap-0.5 rounded-full bg-amber-50 px-1.5 py-0.5 text-[10px] font-medium text-amber-700"
        title="Pineada a zona — el motor no la mueve fuera de su zona"
      >
        <MapPin className="size-3" /> zona
      </span>
    );
  }
  return (
    <span
      className="inline-flex items-center gap-0.5 rounded-full bg-rose-50 px-1.5 py-0.5 text-[10px] font-medium text-rose-700"
      title="Pineada a mesa — el motor nunca la mueve"
    >
      <Lock className="size-3" /> mesa
    </span>
  );
}

// Pill next to the "Hoy ·" indicator that surfaces a tz mismatch between
// the operator's browser and the active sede. Reservation times are already
// rendered in the location's tz; this is purely informational so a remote
// operator doesn't read the wall clock as their own.
function TimezoneOffsetPill() {
  const info = useTimezonePillInfo();
  if (!info) return null;
  return (
    <div
      role="note"
      title={info.tooltip}
      aria-label={info.tooltip}
      className="flex items-center gap-1.5 rounded-full border border-amber-300 bg-amber-50/95 px-3 py-1.5 text-amber-700 shadow-sm backdrop-blur-sm"
    >
      <Globe className="size-3.5" aria-hidden="true" />
      <span className="text-[11px] font-medium">{info.label}</span>
    </div>
  );
}

function useTimezonePillInfo(): { label: string; tooltip: string } | null {
  const locationTz = useActiveLocationTimezone();
  if (!locationTz) return null;
  let guestTz = "";
  try {
    guestTz = dayjs.tz.guess() || "";
  } catch {
    return null;
  }
  if (!guestTz || guestTz === locationTz) return null;
  const now = dayjs();
  const deltaMin = now.tz(guestTz).utcOffset() - now.tz(locationTz).utcOffset();
  const deltaHours = deltaMin / 60;
  const abs = Math.abs(deltaHours);
  const hoursLabel = Number.isInteger(abs) ? `${abs}h` : `${abs.toFixed(1)}h`;
  const direction = deltaHours === 0 ? "" : deltaHours > 0 ? "adelante" : "atrás";
  const offsetSign =
    deltaHours === 0 ? "±0h" : deltaHours > 0 ? `+${hoursLabel}` : `−${hoursLabel}`;
  return {
    label: offsetSign,
    tooltip:
      deltaHours === 0
        ? `Hora del local: ${locationTz}`
        : `Hora del local: ${locationTz} · tu zona (${guestTz}) va ${hoursLabel} ${direction}`,
  };
}

function storeStatusToEngine(s: StoreTableStatus): TableStatus {
  if (s === "free") return "available";
  // "blocked" and "unavailable" both map straight through to MapEngine, which
  // exposes both as renderable statuses with the matching dimmed style.
  return s;
}

const STATUS_COLORS: Record<string, string> = {
  reservada: "#eab308",
  sentado: "#c2410c",
};

/** Update connection colors based on shared reservations */
function syncConnectionColors(engine: MapEngine, liveReservations: Reservation[]) {
  const conns = engine.getConnections();
  const updated: [number, number, string?][] = conns.map(([a, b]) => {
    const shared = liveReservations.find(
      (r) =>
        r.tableIds.includes(a) &&
        r.tableIds.includes(b) &&
        ["sentado", "reservada"].includes(r.status),
    );
    return shared ? [a, b, STATUS_COLORS[shared.status]] : [a, b, undefined];
  });
  engine.setConnections(updated);
}

// ── Main Component ────────────────────────────────────────────

function getSidebarOffset() {
  if (window.innerWidth < 1024) return 0;
  const collapsed = useAppStore.getState().sidebarCollapsed;
  return collapsed ? 64 + 40 : Math.min(380, window.innerWidth * 0.3) + 40;
}

export default function MapCanvasView() {
  const canvasRef = useRef<HTMLCanvasElement>(null);
  const engineRef = useRef<MapEngine | null>(null);
  const shouldAutoFit = useRef(false);
  const dataLoaded = useRef(false);
  const reassignModeRef = useRef<{ reservationId: string; pax: number } | null>(null);
  const [zoom, setZoom] = useState(80);

  // On narrow viewports the "Hoy" pill sits to the left of the brand
  // mark instead of beside it, so we drop the logo below the pill and
  // bump the fit-to-content inset to keep the floor from sliding up
  // underneath it.
  const isMobile = useMediaQuery("(max-width: 639px)");
  const brandMarkTopPx = isMobile ? 64 : 18;
  // Brand mark URL comes from OrganizationSettings.logo on the
  // backend; we render nothing when the tenant hasn't configured one.
  // The fit-to-content inset only applies when the mark is visible.
  const { data: orgSettings } = useOrganizationSettings();
  const brandLogoUrl = orgSettings?.logo_urls?.md ?? orgSettings?.logo_urls?.original ?? null;
  const brandMarkInsetPx = brandLogoUrl ? (isMobile ? 140 : 94) : 0;

  const storeTables = useAppStore((s) => s.tables);
  const storeZones = useAppStore((s) => s.zones);
  const noShowToleranceMinutes = useAppStore((s) => s.restaurantConfig.noShowToleranceMinutes);
  // Reading previewTime here turns the time-travel toggle into a
  // dependency the sync effect below can react to — without this, the
  // canvas keeps showing wall-clock occupation even after the operator
  // picks an hour, because liveReservations alone never changes.
  const previewTime = useAppStore((s) => s.previewTime);
  const previewDate = useAppStore((s) => s.selectedDate);
  const locationId = useAuthStore((s) => s.locationId);
  const mapLocked = useAppStore((s) => (locationId ? !!s.mapLockedByLocation[locationId] : false));
  const {
    setHighlightedReservationId,
    setDetailReservationId,
    focusTableIds,
    setFocusTableIds,
    mapFitRequested,
    mapReassignMode,
  } = useAppStore(
    useShallow((s) => ({
      focusTableIds: s.focusTableIds,
      mapFitRequested: s.mapFitRequested,
      mapReassignMode: s.mapReassignMode,
      setDetailReservationId: s.setDetailReservationId,
      setFocusTableIds: s.setFocusTableIds,
      setHighlightedReservationId: s.setHighlightedReservationId,
    })),
  );
  const liveReservations = useLiveReservations();
  const tablesQuery = useTables();
  const zonesQuery = useZones();
  const isLoadingMap = tablesQuery.isPending || zonesQuery.isPending;

  // Popover state (was module-level signals in Preact)
  const [selectedTableId, setSelectedTableId] = useState<string | null>(null);
  const [showWalkIn, setShowWalkIn] = useState(false);
  const [showDetail, setShowDetail] = useState(false);
  const [showReassign, setShowReassign] = useState(false);
  const [walkInName, setWalkInName] = useState("");
  const [walkInPhone, setWalkInPhone] = useState("");
  const [walkInEmail, setWalkInEmail] = useState("");
  const [walkInGuestId, setWalkInGuestId] = useState<string | null>(null);
  const [walkInPax, setWalkInPax] = useState(2);
  const [popoverAnchor, setPopoverAnchor] = useState<{ x: number; y: number }>({ x: 0, y: 0 });
  const [swapTarget, setSwapTarget] = useState<{
    resA: Reservation;
    resB: Reservation;
    resANames: string;
    resBNames: string;
  } | null>(null);
  const swappableMapRef = useRef<Map<string, Reservation>>(new Map());

  const closePopovers = useCallback(() => {
    setShowWalkIn(false);
    setShowDetail(false);
    setShowReassign(false);
    setSelectedTableId(null);
    setHighlightedReservationId(null);
    if (engineRef.current) {
      engineRef.current.selectedTableId = null;
      engineRef.current.highlightedTableIds = [];
      engineRef.current.requestRender();
    }
  }, [setHighlightedReservationId]);

  // ── Engine lifecycle ─────────────────────────────────────────

  useEffect(() => {
    const canvas = canvasRef.current;
    if (!canvas || !locationId) {
      return;
    }

    const cam = loadCamera(locationId);
    const engine = new MapEngine(canvas, {
      mode: "view",
      worldBounds: WORLD_BOUNDS,
      ...(cam
        ? { initialOffsetX: cam.offsetX, initialOffsetY: cam.offsetY, initialScale: cam.scale }
        : {}),
    });

    const layout = buildLayout(storeTables, storeZones);
    engine.load(layout);

    // Defer auto-fit to the data-loaded effect when no saved camera exists.
    // Fitting here is useless because storeTables is usually empty on mount
    // (the API hasn't resolved yet) and fitToContent bails early.
    shouldAutoFit.current = !cam;
    // Each locationId change destroys and rebuilds this engine — reset the
    // data-loaded guard so the next layout load actually fires.
    dataLoaded.current = false;

    // Apply real-time statuses + timers + connection colors
    for (const t of engine.tables) {
      t.status = storeStatusToEngine(getTableStatus(t.id));
      const res = getTableReservation(t.id);
      if (t.status === "occupied" && res?.endTime) {
        t.timer = minutesRemaining(res.endTime, res.endDate ?? res.date);
      }
      const nsMin =
        t.status === "reserved" && res
          ? minutesUntilNoShow(res.time, noShowToleranceMinutes, res.date)
          : null;
      t.noShowTimer = nsMin ?? -1;
    }
    syncConnectionColors(engine, liveReservations);
    engine.requestRender();

    // Camera save
    let cameraSaveTimer: ReturnType<typeof setTimeout>;
    function scheduleCameraSave() {
      clearTimeout(cameraSaveTimer);
      cameraSaveTimer = setTimeout(() => saveCamera(locationId, engine.camera), 300);
    }

    engine.on("zoom", (pct) => {
      setZoom(pct);
      scheduleCameraSave();
    });

    // Table click → open popover + highlight grouped tables
    engine.on("table:select", (table) => {
      if (!table) {
        engine.highlightedTableIds = [];
        closePopovers();
        return;
      }

      // Disabled tables (or any table whose zone is disabled) cannot take new
      // bookings or walk-ins. Render the rejection inline so operators see
      // why the click did nothing instead of guessing.
      if (table.status === "unavailable") {
        engine.highlightedTableIds = [];
        closePopovers();
        sileo.warning({
          description: "Esta mesa está deshabilitada y no acepta reservas ni walk-ins.",
        });
        return;
      }

      // ── Reassign mode: click a selectable table to assign it ──
      const rm = reassignModeRef.current;
      if (rm && table.status === "selectable") {
        let assignIds = [table.id];

        // If table alone doesn't fit, add available neighbors until pax is met.
        // ``table`` here is the engine ``MapTable`` (carries ``capacity`` only).
        if (table.capacity < rm.pax) {
          const storeTable = useAppStore.getState().tables.find((st) => st.id === table.id);
          if (storeTable?.neighbors.length) {
            const freeNeighbors = storeTable.neighbors
              .map((nid) => engine.tables.find((et) => et.id === nid))
              .filter((n) => n && n.status === "selectable")
              .toSorted((a, b) => b!.capacity - a!.capacity);
            let total = table.capacity;
            for (const n of freeNeighbors) {
              if (total >= rm.pax) break;
              assignIds.push(n!.id);
              total += n!.capacity;
            }
          }
        }

        // Stay in reassign mode if staff backed out of a walk-ins-only zone,
        // so "Elegir otra mesa" means exactly that.
        void reassignTable(rm.reservationId, assignIds).then((ok) => {
          if (!ok) return;
          useAppStore.getState().setMapReassignMode(null);
          useAppStore.getState().setSidebarCollapsed(false);
          setFocusTableIdsAction(assignIds);
          engine.highlightedTableIds = assignIds;
          engine.requestRender();
        });
        return;
      }
      // ── Swap mode: click a swappable table to open confirmation ──
      if (rm && table.status === "swappable") {
        const resA = useAppStore.getState().reservations.find((r) => r.id === rm.reservationId);
        const resB = swappableMapRef.current.get(table.id);
        const st = useAppStore.getState().tables;
        if (resA && resB) {
          setSwapTarget({
            resA,
            resB,
            resANames: resA.tableIds
              .map((id) => st.find((t) => t.id === id)?.name || "Mesa")
              .join(" + "),
            resBNames: resB.tableIds
              .map((id) => st.find((t) => t.id === id)?.name || "Mesa")
              .join(" + "),
          });
        }
        return;
      }
      if (rm) return; // ignore clicks on other tables in reassign mode
      const screen = engine.camera.worldToScreen(table.x + table.w / 2, table.y + table.h / 2);
      setPopoverAnchor({ x: screen.x, y: screen.y });
      setSelectedTableId(table.id);

      // Highlight all tables in the same reservation
      const reservation = getTableReservation(table.id);
      engine.highlightedTableIds = reservation
        ? reservation.tableIds.filter((id) => id !== table.id)
        : [];

      const status = getTableStatus(table.id);
      if (status === "free") {
        // Time-travel mode: clicking a free mesa is the operator
        // saying "I want to book this slot" — open the in-sidebar
        // Nueva reserva surface preseeded with the simulated time
        // and this mesa instead of seating a walk-in, which only
        // makes sense in real-time.
        const sim = useAppStore.getState().previewTime;
        if (sim) {
          useAppStore.getState().openNewReservation({
            time: sim,
            tableIds: [table.id],
          });
          setShowWalkIn(false);
          setShowDetail(false);
          return;
        }
        const currentTable = useAppStore.getState().tables.find((t) => t.id === table.id);
        setShowWalkIn(true);
        setShowDetail(false);
        // Default to the table's min so the operator never has to bump down
        // before adjusting up — the panel already clamps further inputs.
        const tMin = Number.isFinite(currentTable?.minCapacity) ? currentTable!.minCapacity : 1;
        const tMax = currentTable?.maxCapacity ?? 2;
        setWalkInPax(Math.max(tMin, Math.min(tMax, 2)));
        setWalkInName("");
        setWalkInPhone("");
        setWalkInEmail("");
        setWalkInGuestId(null);
      } else {
        setShowDetail(true);
        setShowWalkIn(false);
        if (reservation) {
          setHighlightedReservationId(reservation.id);
          setTimeout(() => {
            const card = document.querySelector(`[data-reservation-id="${reservation.id}"]`);
            card?.scrollIntoView({ behavior: "smooth", block: "nearest" });
          }, 50);
        }
      }
    });

    // Long-press (touch) or right-click (mouse) → anchor the popover at the
    // pointer location instead of the table centroid. Matches the spec's
    // "Open contextual actions" requirement for coarse pointers.
    engine.on("table:contextmenu", ({ table, x, y }) => {
      if (table.status === "unavailable") {
        sileo.warning({
          description: "Esta mesa está deshabilitada y no acepta reservas ni walk-ins.",
        });
        return;
      }
      const rect = canvasRef.current?.getBoundingClientRect();
      setPopoverAnchor({ x: x - (rect?.left || 0), y: y - (rect?.top || 0) });
      setSelectedTableId(table.id);
      const status = getTableStatus(table.id);
      if (status === "free") {
        // Same simulation guard as on left-click — under preview we
        // never open the walk-in panel, since "sentar walk-in" is a
        // wall-clock-only operation. The in-sidebar new-reservation
        // surface receives the seed and renders inline.
        const sim = useAppStore.getState().previewTime;
        if (sim) {
          useAppStore.getState().openNewReservation({
            time: sim,
            tableIds: [table.id],
          });
          setShowWalkIn(false);
          setShowDetail(false);
          return;
        }
        const storeTable = storeTables.find((t) => t.id === table.id);
        setShowWalkIn(true);
        setShowDetail(false);
        setWalkInPax(storeTable?.maxCapacity || 4);
        setWalkInName("");
        setWalkInPhone("");
        setWalkInEmail("");
        setWalkInGuestId(null);
      } else {
        setShowDetail(true);
        setShowWalkIn(false);
      }
    });

    canvas.addEventListener("mouseup", scheduleCameraSave);
    canvas.addEventListener("touchend", scheduleCameraSave);

    engineRef.current = engine;
    setZoom(engine.camera.zoomPercent);

    return () => {
      clearTimeout(cameraSaveTimer);
      canvas.removeEventListener("mouseup", scheduleCameraSave);
      canvas.removeEventListener("touchend", scheduleCameraSave);
      // Flush the last camera position so a sede switch (or the mode toggle)
      // never loses it, even if the user pans and toggles within the 300ms
      // debounce window. Scoped to the location this engine was serving.
      saveCamera(locationId, engine.camera);
      engine.destroy();
      engineRef.current = null;
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [locationId]);

  // ── Sync mapLocked flag to engine ───────────────────────────
  useEffect(() => {
    engineRef.current?.setMapLocked(mapLocked);
  }, [mapLocked]);

  // ── Reload engine when API data arrives ─────────────────────
  useEffect(() => {
    const engine = engineRef.current;
    if (!engine || storeTables.length === 0 || storeZones.length === 0 || dataLoaded.current)
      return;
    dataLoaded.current = true;
    const cam = {
      offsetX: engine.camera.offsetX,
      offsetY: engine.camera.offsetY,
      scale: engine.camera.scale,
    };
    engine.load(buildLayout(storeTables, storeZones));
    if (shouldAutoFit.current) {
      shouldAutoFit.current = false;
      // Reserve the top strip the SEIM·SEIM lockup sits in
      // (top:18 + height:61 + 15 px breathing room) so
      // fit-to-content never centres the floor on top of it.
      engine.fitToContent(80, getSidebarOffset(), brandMarkInsetPx);
      if (locationId) saveCamera(locationId, engine.camera);
    } else {
      Object.assign(engine.camera, cam);
    }
    // Apply statuses
    for (const t of engine.tables) {
      t.status = storeStatusToEngine(getTableStatus(t.id));
      const res = getTableReservation(t.id);
      if (t.status === "occupied" && res?.endTime) {
        t.timer = minutesRemaining(res.endTime, res.endDate ?? res.date);
      }
      const nsMin =
        t.status === "reserved" && res
          ? minutesUntilNoShow(res.time, noShowToleranceMinutes, res.date)
          : null;
      t.noShowTimer = nsMin ?? -1;
      t.icon = res?.isVip ? "vip" : "none";
    }
    syncConnectionColors(engine, liveReservations);
    engine.requestRender();
  }, [storeTables, storeZones, liveReservations]);

  // ── Sync statuses on store changes ───────────────────────────

  useEffect(() => {
    const engine = engineRef.current;
    if (!engine) {
      return;
    }
    for (const t of engine.tables) {
      t.status = storeStatusToEngine(getTableStatus(t.id));
      const res = getTableReservation(t.id);
      if (t.status === "occupied" && res?.endTime) {
        t.timer = minutesRemaining(res.endTime, res.endDate ?? res.date);
      } else {
        t.timer = 0;
      }
      const nsMin =
        t.status === "reserved" && res
          ? minutesUntilNoShow(res.time, noShowToleranceMinutes, res.date)
          : null;
      t.noShowTimer = nsMin ?? -1;
      t.icon = res?.isVip ? "vip" : "none";
    }
    // Mirror the time-travel state onto the canvas: soft amber wash
    // for the bg, default theme otherwise. The renderer reads
    // ``bgOverride`` each frame, so the next ``requestRender`` below
    // picks it up.
    engine.bgOverride = previewTime ? "#fdf6e0" : null;
    syncConnectionColors(engine, liveReservations);
    engine.requestRender();
  }, [liveReservations, previewTime]);

  // ── Recalculate canvas timers every 60 s ────────────────────
  useEffect(() => {
    const id = setInterval(() => {
      const engine = engineRef.current;
      if (!engine) return;
      for (const t of engine.tables) {
        const res = getTableReservation(t.id);
        t.timer =
          t.status === "occupied" && res?.endTime
            ? minutesRemaining(res.endTime, res.endDate ?? res.date)
            : 0;
        const nsMin =
          t.status === "reserved" && res
            ? minutesUntilNoShow(res.time, noShowToleranceMinutes, res.date)
            : null;
        t.noShowTimer = nsMin ?? -1;
      }
      engine.requestRender();
    }, 60_000);
    return () => clearInterval(id);
  }, [noShowToleranceMinutes]);

  // ── Keep reassign ref in sync ───────────────────────────────
  reassignModeRef.current = mapReassignMode;

  // ── Reassign mode — highlight available tables blue ──────────

  useEffect(() => {
    const engine = engineRef.current;
    if (!engine) return;
    if (mapReassignMode) {
      const pax = mapReassignMode.pax;
      const allStoreTables = useAppStore.getState().tables;

      // Build a set of table IDs that can host the reservation (alone or with neighbors)
      const selectableIds = new Set<string>();
      for (const t of engine.tables) {
        // ``engine.tables`` items are ``MapTable`` (carry ``capacity`` only).
        if (storeStatusToEngine(getTableStatus(t.id)) !== "available") continue;
        if (t.capacity >= pax) {
          selectableIds.add(t.id);
          continue;
        }
        const storeTable = allStoreTables.find((st) => st.id === t.id);
        if (!storeTable?.neighbors.length) continue;
        const neighborCap = storeTable.neighbors.reduce((sum, nid) => {
          const neighbor = engine.tables.find((et) => et.id === nid);
          if (neighbor && storeStatusToEngine(getTableStatus(nid)) === "available") {
            return sum + neighbor.capacity;
          }
          return sum;
        }, 0);
        if (t.capacity + neighborCap >= pax) {
          selectableIds.add(t.id);
        }
      }

      // Build swappable map: occupied/reserved tables whose reservation can swap
      const newSwappableMap = new Map<string, Reservation>();
      const resA = useAppStore
        .getState()
        .reservations.find((r) => r.id === mapReassignMode.reservationId);
      if (resA) {
        const resACapacity = resA.tableIds.reduce((sum, id) => {
          const st = allStoreTables.find((t) => t.id === id);
          return sum + (st?.maxCapacity || 0);
        }, 0);
        const candidates = useAppStore
          .getState()
          .reservations.filter(
            (r) =>
              r.id !== resA.id &&
              r.date === resA.date &&
              ["reservada", "sentado"].includes(r.status) &&
              ["reservada", "sentado"].includes(resA.status) &&
              r.tableIds.length > 0,
          );
        for (const resB of candidates) {
          const resBCapacity = resB.tableIds.reduce((sum, id) => {
            const st = allStoreTables.find((t) => t.id === id);
            return sum + (st?.maxCapacity || 0);
          }, 0);
          if (pax <= resBCapacity && resB.pax <= resACapacity) {
            for (const tid of resB.tableIds) {
              newSwappableMap.set(tid, resB);
            }
          }
        }
      }
      swappableMapRef.current = newSwappableMap;

      for (const t of engine.tables) {
        const baseStatus = storeStatusToEngine(getTableStatus(t.id));
        if (selectableIds.has(t.id)) {
          t.status = "selectable";
        } else if (newSwappableMap.has(t.id)) {
          t.status = "swappable";
        } else {
          t.status = baseStatus;
        }
      }
    } else {
      swappableMapRef.current = new Map();
      for (const t of engine.tables) {
        if (t.status === "selectable" || t.status === "swappable") {
          t.status = storeStatusToEngine(getTableStatus(t.id));
        }
      }
    }
    engine.requestRender();
  }, [mapReassignMode]);

  // ── Focus on specific tables ─────────────────────────────────

  useEffect(() => {
    const engine = engineRef.current;
    if (!engine || focusTableIds.length === 0) return;

    engine.focusTables(focusTableIds, 300, getSidebarOffset(), 1.0);
    engine.highlightedTableIds = focusTableIds;
    engine.requestRender();
    if (locationId) saveCamera(locationId, engine.camera);
    setFocusTableIds([]);
  }, [focusTableIds, setFocusTableIds, locationId]);

  // ── Fit to content ──────────────────────────────────────────────

  useEffect(() => {
    const engine = engineRef.current;
    if (!engine || mapFitRequested === 0) return;

    engine.highlightedTableIds = [];
    engine.fitToContent(80, getSidebarOffset(), brandMarkInsetPx);
    if (locationId) saveCamera(locationId, engine.camera);
  }, [mapFitRequested, locationId]);

  // ── Popover position (viewport-fixed) ────────────────────────

  const rect = canvasRef.current?.getBoundingClientRect();
  const vw = window.innerWidth || 900;
  const vh = window.innerHeight || 600;
  const POPOVER_W = 270;
  const GAP = 16;
  const anchorVX = (rect?.left || 0) + popoverAnchor.x;
  const anchorVY = (rect?.top || 0) + popoverAnchor.y;
  let popLeft = anchorVX + GAP;
  if (popLeft + POPOVER_W > vw - 12) {
    popLeft = anchorVX - POPOVER_W - GAP;
  }
  popLeft = Math.max(12, Math.min(popLeft, vw - POPOVER_W - 12));
  let popTop = anchorVY - 40;
  popTop = Math.max(12, Math.min(popTop, vh - 380));

  return (
    <div className="flex-1 min-h-0 relative overflow-hidden flex flex-col">
      {/* Canvas — full area. Wrapper fill matches the map engine's
          ``THEME.bg`` so there's no flash of a neutral gray during
          camera resizes or while the canvas re-renders. */}
      <div className="flex-1 min-h-0 relative" style={{ background: "#fbf7ef" }}>
        {/* Organization brand lockup — pinned to the top center of
            the canvas. Source is OrganizationSettings.logo on the
            backend; we render nothing when the tenant hasn't
            configured one. ``pointer-events-none`` keeps drag / tap
            untouched. */}
        {brandLogoUrl && (
          <img
            src={brandLogoUrl}
            alt=""
            className="pointer-events-none absolute left-1/2 -translate-x-1/2 select-none"
            style={{
              top: `${brandMarkTopPx}px`,
              height: "61px",
              width: "auto",
              zIndex: 2,
            }}
          />
        )}
        <canvas
          ref={canvasRef}
          style={{
            cursor: "grab",
            display: "block",
            height: "100%",
            position: "relative",
            width: "100%",
            zIndex: 1,
          }}
        />

        {/* Live-today indicator / simulated-time toggle.
            Doubles as the affordance to exit time-travel: when previewTime
            is set, the pill turns amber, shows the simulated hour, and
            clicking anywhere on it returns to "Hoy". */}
        <div className="absolute top-4 left-4 z-10 flex items-center gap-2">
          {previewTime ? (
            <button
              type="button"
              onClick={() => useAppStore.getState().setPreviewTime(null)}
              title="Volver a hoy"
              className="flex items-center gap-1.5 rounded-full border border-amber-300 bg-amber-100/95 px-3 py-1.5 text-amber-900 shadow-sm backdrop-blur-sm transition hover:bg-amber-200/95"
            >
              <Clock className="size-3" />
              <span className="text-[11px] font-semibold">
                Simulando ·{" "}
                {previewDate !== todayStr() ? `${dayjs(previewDate).format("ddd D MMM")} · ` : ""}
                {previewTime}
              </span>
              <span className="ml-1 rounded-full bg-amber-700 px-1.5 py-0.5 text-[9px] font-bold uppercase tracking-wide text-white">
                volver a hoy
              </span>
            </button>
          ) : (
            <div className="flex items-center gap-1.5 rounded-full border border-border bg-white/90 px-3 py-1.5 shadow-sm backdrop-blur-sm pointer-events-none">
              <span className="relative flex size-2">
                <span className="absolute inline-flex h-full w-full animate-ping rounded-full bg-green-500 opacity-60" />
                <span className="relative inline-flex size-2 rounded-full bg-green-500" />
              </span>
              <span className="font-serif text-[11px] font-medium text-text-primary">
                Hoy · {dayjs().format("ddd D MMM")}
              </span>
            </div>
          )}
          <TimezoneOffsetPill />
        </div>

        {/* Reassign mode banner */}
        {mapReassignMode && !swapTarget && (
          <div className="absolute top-4 left-1/2 z-20 -translate-x-1/2 flex items-center gap-3 rounded-xl bg-blue-600 px-4 py-2.5 text-white shadow-lg">
            <span className="text-sm font-medium">
              <span className="inline-block w-2.5 h-2.5 rounded-sm bg-blue-300 mr-1 align-middle" />{" "}
              Asignar
              <span className="mx-1.5 opacity-50">·</span>
              <span className="inline-block w-2.5 h-2.5 rounded-sm bg-purple-400 mr-1 align-middle" />{" "}
              Intercambiar
            </span>
            <button
              type="button"
              onClick={() => {
                useAppStore.getState().setMapReassignMode(null);
                useAppStore.getState().setSidebarCollapsed(false);
              }}
              className="rounded-lg bg-white/20 px-2.5 py-1 text-xs font-medium transition hover:bg-white/30"
            >
              Cancelar
            </button>
          </div>
        )}

        {isLoadingMap && (
          <div className="absolute inset-0 z-10 flex items-center justify-center bg-[#f2f2f0]/70 backdrop-blur-sm pointer-events-none">
            <Spinner className="size-8 text-text-secondary" />
          </div>
        )}

        <MapToolbar>
          <MapToolbarButton
            onClick={() => {
              engineRef.current?.fitToContent(80, getSidebarOffset(), brandMarkInsetPx);
              if (engineRef.current && locationId) {
                saveCamera(locationId, engineRef.current.camera);
              }
            }}
            label="Ajustar al mapa"
          >
            <Shrink className="size-4" />
          </MapToolbarButton>
          <span className="text-[11px] font-mono text-gray-400 tabular-nums min-w-9 text-center px-1">
            {zoom}%
          </span>

          <MapToolbarDivider />

          <MapToolbarLockToggle />
          <MapToolbarModeToggle />
        </MapToolbar>

        {/* Popover */}
        {(showWalkIn || showDetail) && (
          <>
            {/* No click-away overlay: it would sit on top of the canvas and
                swallow tap-and-drag gestures (drags don't fire ``onClick``),
                making the map feel frozen. The engine already emits
                ``table:select(null)`` whenever the user taps an empty area
                of the canvas, which calls ``closePopovers`` — same effect,
                but the same gesture also starts a pan. */}

            {/* Desktop: anchored popover */}
            <div
              className="hidden lg:block fixed z-50 animate-pop-in"
              style={{ left: popLeft, top: popTop, width: POPOVER_W }}
              onClick={(e: React.MouseEvent) => e.stopPropagation()}
            >
              {showWalkIn && (
                <WalkInPanel
                  selectedTableId={selectedTableId}
                  walkInPax={walkInPax}
                  setWalkInPax={setWalkInPax}
                  walkInName={walkInName}
                  setWalkInName={setWalkInName}
                  walkInPhone={walkInPhone}
                  setWalkInPhone={setWalkInPhone}
                  walkInEmail={walkInEmail}
                  setWalkInEmail={setWalkInEmail}
                  walkInGuestId={walkInGuestId}
                  setWalkInGuestId={setWalkInGuestId}
                  storeTables={storeTables}
                  onClose={closePopovers}
                />
              )}
              {showDetail && (
                <DetailPanel
                  selectedTableId={selectedTableId}
                  setSelectedTableId={setSelectedTableId}
                  storeTables={storeTables}
                  showReassign={showReassign}
                  setShowReassign={setShowReassign}
                  setDetailReservationId={setDetailReservationId}
                  onClose={closePopovers}
                />
              )}
            </div>

            {/* Tablet: bottom sheet */}
            <div
              className="lg:hidden fixed inset-x-0 bottom-0 z-50 px-4 pb-4 pt-2"
              onClick={(e: React.MouseEvent) => e.stopPropagation()}
            >
              <div className="max-w-100 mx-auto">
                {showWalkIn && (
                  <WalkInPanel
                    selectedTableId={selectedTableId}
                    walkInPax={walkInPax}
                    setWalkInPax={setWalkInPax}
                    walkInName={walkInName}
                    setWalkInName={setWalkInName}
                    walkInPhone={walkInPhone}
                    setWalkInPhone={setWalkInPhone}
                    walkInEmail={walkInEmail}
                    setWalkInEmail={setWalkInEmail}
                    walkInGuestId={walkInGuestId}
                    setWalkInGuestId={setWalkInGuestId}
                    storeTables={storeTables}
                    onClose={closePopovers}
                  />
                )}
                {showDetail && (
                  <DetailPanel
                    selectedTableId={selectedTableId}
                    setSelectedTableId={setSelectedTableId}
                    storeTables={storeTables}
                    showReassign={showReassign}
                    setShowReassign={setShowReassign}
                    setDetailReservationId={setDetailReservationId}
                    onClose={closePopovers}
                  />
                )}
              </div>
            </div>
          </>
        )}
      </div>

      <style>{`
        .animate-pop-in { animation: popIn 0.1s ease }
        @keyframes popIn { from { opacity:0;transform:scale(0.96) } to { opacity:1;transform:scale(1) } }
      `}</style>

      {/* Swap confirmation modal */}
      <AlertDialog
        open={!!swapTarget}
        onOpenChange={(open) => {
          if (!open) setSwapTarget(null);
        }}
      >
        <AlertDialogContent>
          <AlertDialogHeader>
            <AlertDialogTitle>¿Intercambiar mesas?</AlertDialogTitle>
            <AlertDialogDescription>
              <strong>{swapTarget?.resA.guestName}</strong> ({swapTarget?.resA.pax}p ·{" "}
              {swapTarget?.resANames}) intercambiará mesas con{" "}
              <strong>{swapTarget?.resB.guestName}</strong> ({swapTarget?.resB.pax}p ·{" "}
              {swapTarget?.resBNames})
            </AlertDialogDescription>
          </AlertDialogHeader>
          <AlertDialogFooter>
            <AlertDialogCancel onClick={() => setSwapTarget(null)}>Cancelar</AlertDialogCancel>
            <AlertDialogAction
              onClick={() => {
                if (swapTarget) {
                  const target = swapTarget;
                  setSwapTarget(null);
                  void swapTables(target.resA.id, target.resB.id).then((ok) => {
                    if (!ok) return;
                    useAppStore.getState().setMapReassignMode(null);
                    useAppStore.getState().setSidebarCollapsed(false);
                    setFocusTableIdsAction([...target.resB.tableIds]);
                  });
                }
              }}
            >
              Intercambiar
            </AlertDialogAction>
          </AlertDialogFooter>
        </AlertDialogContent>
      </AlertDialog>
    </div>
  );
}

// ── Walk-In Panel ─────────────────────────────────────────────

interface WalkInPanelProps {
  selectedTableId: string | null;
  walkInPax: number;
  setWalkInPax: (v: number) => void;
  walkInName: string;
  setWalkInName: (v: string) => void;
  walkInPhone: string;
  setWalkInPhone: (v: string) => void;
  walkInEmail: string;
  setWalkInEmail: (v: string) => void;
  walkInGuestId: string | null;
  setWalkInGuestId: (v: string | null) => void;
  storeTables: Table[];
  onClose: () => void;
}

function WalkInPanel({
  selectedTableId,
  walkInPax,
  setWalkInPax,
  walkInName,
  setWalkInName,
  walkInPhone,
  setWalkInPhone,
  walkInEmail,
  setWalkInEmail,
  walkInGuestId,
  setWalkInGuestId,
  storeTables,
  onClose,
}: WalkInPanelProps) {
  const table = storeTables.find((t) => t.id === selectedTableId);
  const { data: tableGroups } = useTableGroups();
  const {
    data: walkinPlan,
    isPending: walkinPlanPending,
    isError: walkinPlanErrored,
  } = usePlanWalkin(table?.id ?? "", walkInPax);
  const { data: locationSettings } = useLocationSettings();
  const locationTz = useActiveLocationTimezone();
  const liveReservations = useLiveReservations();
  const [showOverbookConfirm, setShowOverbookConfirm] = useState(false);

  if (!table) {
    return null;
  }

  // Walk-ins can only use tables that are physically free right now — busy
  // tables (occupied/reserved/blocked) drop out of the math, even though they
  // still belong to the same connected component / declared group.
  const connectedIds = findConnectedGroup(table.id, storeTables);
  const freeConnectedIds = connectedIds.filter((id) => getTableStatus(id) === "free");
  const matchingGroup = tableGroups?.find(
    (g) =>
      g.tables.length === connectedIds.length && g.tables.every((id) => connectedIds.includes(id)),
  );
  // The declared group only counts when every one of its tables is free —
  // otherwise we'd be promising a capacity that can't be physically delivered.
  const groupFullyFree =
    matchingGroup && matchingGroup.tables.every((id) => freeConnectedIds.includes(id));
  const freeSumMax = freeConnectedIds.reduce((sum, id) => {
    const t = storeTables.find((tt) => tt.id === id);
    return sum + (t?.maxCapacity ?? 0);
  }, 0);

  // Capacity priority:
  //   1. ``TableGroup`` declared max — only when the entire group is free.
  //   2. Sum of every *free* neighbour reachable from this table.
  //   3. Backend walk-in plan's ``max_available_pax`` (it filters occupancy too).
  //   4. The tapped table's own max as the final floor.
  const maxCapacity =
    (groupFullyFree ? matchingGroup.max_capacity : null) ??
    (freeSumMax > 0 ? freeSumMax : null) ??
    walkinPlan?.max_available_pax ??
    table.maxCapacity;

  // Walk-ins may seat below the mesa's nominal min (e.g. 1 person at a
  // 2-top): the operator is physically placing them, so the stepper floor
  // is 1. The popover still OPENS at the mesa's natural size (set where
  // it's initialised), so the common case is one tap; decrementing past
  // the mesa min is allowed and the backend surfaces a confirmable
  // "capacidad desaprovechada" override on save.
  const minCapacity = 1;

  // Reflect what the backend will actually seat. ``placeholderData`` on the
  // ``usePlanWalkin`` query keeps the previous response while a new pax value
  // is in flight, so the chip doesn't flicker on every ± click.
  //
  // BUT: ``placeholderData`` also surfaces the PREVIOUS mesa's plan when
  // the operator hops mesa-to-mesa without closing the popover. Trust
  // the plan only when its ``anchor_table_id`` matches the picked mesa;
  // otherwise treat as "no plan yet" so combine-detection and the
  // overbook check fall back to ``[table.id]`` and don't render a
  // stale answer.
  const planIsForCurrentTable = walkinPlan?.anchor_table_id === table.id;
  const planTableIds = planIsForCurrentTable ? (walkinPlan?.table_ids ?? []) : [];
  // "Stale" gates the submit button. The plan is genuinely stale only
  // while we're still loading. A 409 from ``plan_walkin`` (anchor not
  // free for the full default slot — usually because of an upcoming
  // reservation inside the window, which is exactly the case this
  // panel handles) is a SETTLED state, not a stale one: fall back to
  // ``[table.id]`` for clash detection and let the operator proceed.
  const planIsStale = walkinPlanPending || (!planIsForCurrentTable && !walkinPlanErrored);
  const willCombine = planTableIds.length > 1;
  const combineNames = planTableIds
    .filter((id) => id !== table.id)
    .map((id) => storeTables.find((t) => t.id === id)?.name ?? "Mesa")
    .filter((name, i, arr) => arr.indexOf(name) === i);

  // ── Possible-overbooking warning ────────────────────────────────────
  // The picked mesa is free *right now* (otherwise the walk-in panel
  // wouldn't have opened), but a BOOKED reservation might be arriving
  // before the walk-in's typical duration ends. Surface those so the
  // operator decides consciously whether to seat — "esta mesa la
  // necesitamos en 1h, ¿todavía?".
  const walkInDurationMin = locationSettings?.default_reservation_duration_minutes ?? 180;
  const seatedTableIds = planTableIds.length > 0 ? planTableIds : [table.id];
  const checkWindowEnd = dayjs().add(walkInDurationMin, "minute");
  const upcomingClashes = liveReservations
    .filter((r) => r.status === "reservada")
    .filter((r) => r.tableIds.some((tid) => seatedTableIds.includes(tid)))
    .map((r) => {
      const startsAtLocal = parseInTz(`${r.date} ${r.time}`, "YYYY-MM-DD h:mm A", locationTz);
      return { reservation: r, startsAtLocal };
    })
    .filter(({ startsAtLocal }) => {
      const fromNow = startsAtLocal.diff(dayjs(), "minute");
      // Future-only (skip ones that should already be seated and skip
      // ones past our walk-in's window) and within the duration budget.
      return fromNow > 0 && startsAtLocal.isBefore(checkWindowEnd);
    })
    .toSorted((a, b) => a.startsAtLocal.valueOf() - b.startsAtLocal.valueOf());

  // When upcoming reservations clash on the picked mesa(s), the
  // walk-in must end before the next one arrives — otherwise the
  // backend's ``ensure_tables_available`` 409s. We cap ``endTime`` at
  // ``earliestClash.startsAt - turnover_buffer`` so the two bookings
  // physically don't overlap (the buffer protects against turnover
  // time the kitchen / hosts need between parties).
  //
  // Floor: ``cappedEndIsViable`` is False when the cap would land in
  // the past or within 5 min of now — at that point we skip the cap
  // (no point seating a walk-in for negative-duration) and let the
  // operator decide via the alert + modal whether to seat anyway,
  // accepting that the next reservation may be displaced.
  const MIN_USABLE_WALKIN_MINUTES = 5;
  const turnoverBufferMin = locationSettings?.turnover_buffer_minutes ?? 15;
  const earliestClashStart = upcomingClashes[0]?.startsAtLocal;
  const cappedEnd = earliestClashStart?.subtract(turnoverBufferMin, "minute");
  const cappedEndIsViable =
    cappedEnd !== undefined && cappedEnd.diff(dayjs(), "minute") >= MIN_USABLE_WALKIN_MINUTES;
  const cappedEndTime = cappedEndIsViable ? cappedEnd.format("h:mm A") : undefined;

  const commitSeat = () => {
    // The backend ``plan_walkin`` is the single source of truth for which
    // tables get seated. Groups are composable, not rigid — the staff joins
    // physically only the tables that the plan returned.
    const idsToSeat = walkinPlan?.table_ids?.length ? walkinPlan.table_ids : [table.id];
    // Always send an explicit ``endTime`` matching the duration we used
    // to detect clashes above. Without this, ``apiCreateReservation``
    // falls back to a hardcoded 3 h tail — so a location whose
    // ``default_reservation_duration_minutes`` is shorter (e.g. 150)
    // ended up with a frontend that thought 0..150 was the relevant
    // window but a backend slot of 0..180. Reservations sitting in
    // that 30 min "blind spot" gap would 409 with no prior warning.
    // ``cappedEndTime`` (set when there's a clash) overrides.
    const defaultEnd = dayjs().add(walkInDurationMin, "minute").format("h:mm A");
    const endTime = cappedEndTime ?? defaultEnd;
    createWalkIn(idsToSeat, walkInPax, walkInName, locationTz, {
      guestId: walkInGuestId || undefined,
      guestPhone: walkInPhone || undefined,
      guestEmail: walkInEmail || undefined,
      endTime,
    });
    onClose();
  };

  const handleSubmit = () => {
    // Force an explicit acknowledgement when the picked mesa(s) have
    // an upcoming reservation inside the walk-in's typical window.
    // The amber alert above already shows the conflict; the modal is
    // the "are you really sure?" gate so it can't be missed by a
    // hurried tap.
    if (upcomingClashes.length > 0) {
      setShowOverbookConfirm(true);
      return;
    }
    commitSeat();
  };

  // Live guest lookup: as the operator types in the "Nombre o teléfono"
  // input, search existing Guests so a repeat visitor can be picked in
  // one tap (vs. re-typing phone + email from scratch). Disabled when
  // a guest was already picked or the query is too short to be useful.
  const lookupQuery = walkInName.trim();
  const lookupEnabled = !walkInGuestId && lookupQuery.length >= 2;
  const { data: guestMatches } = useGuests(lookupEnabled ? lookupQuery : undefined);
  const visibleMatches = lookupEnabled ? (guestMatches?.slice(0, 4) ?? []) : [];

  return (
    <div
      className="bg-white p-4 rounded-2xl w-80 text-sm overflow-y-auto"
      style={{
        boxShadow: "0 8px 28px rgba(0,0,0,0.14)",
        maxHeight: "calc(100dvh - 80px)",
      }}
    >
      <div className="flex justify-between items-center mb-4 gap-3">
        <div className="flex min-w-0 flex-1 items-center gap-3">
          <span className="flex size-11 shrink-0 items-center justify-center rounded-xl bg-muted text-foreground">
            <UserPlus className="size-6" />
          </span>
          <div className="min-w-0 flex-1">
            <h3 className="text-lg font-semibold text-gray-900 truncate">{table.name}</h3>
            <p className="text-xs text-text-muted truncate inline-flex items-center gap-1">
              <UserPlus className="size-3" /> Walk-in
            </p>
          </div>
        </div>
        <button
          type="button"
          aria-label="Cerrar"
          className="flex size-9 shrink-0 items-center justify-center rounded-full text-gray-500 hover:bg-gray-100 hover:text-gray-800"
          onClick={onClose}
        >
          <X className="size-5" />
        </button>
      </div>

      <div className="flex flex-col gap-3">
        <div className="flex flex-col gap-1.5">
          <Label className="text-xs text-gray-500 font-medium uppercase tracking-wide">
            Personas (mín. {minCapacity} · máx. {maxCapacity})
          </Label>
          <div className="flex items-center gap-2">
            <Button
              variant="outline"
              aria-label="Disminuir personas"
              className="size-11 shrink-0 p-0"
              onClick={() => setWalkInPax(Math.max(minCapacity, walkInPax - 1))}
              disabled={walkInPax <= minCapacity}
            >
              <Minus className="size-5" />
            </Button>
            <Input
              type="text"
              inputMode="numeric"
              value={walkInPax}
              onChange={(e) => {
                const v = parseInt(e.target.value);
                if (v) {
                  setWalkInPax(Math.max(minCapacity, Math.min(maxCapacity, v)));
                }
              }}
              className="h-11 flex-1 text-center text-base font-semibold tabular-nums"
            />
            <Button
              variant="outline"
              aria-label="Aumentar personas"
              className="size-11 shrink-0 p-0"
              onClick={() => setWalkInPax(Math.min(maxCapacity, walkInPax + 1))}
              disabled={walkInPax >= maxCapacity}
            >
              <Plus className="size-5" />
            </Button>
          </div>
          {willCombine && (
            <div className="mt-1 flex items-start gap-2.5 rounded-xl border border-blue-200 bg-blue-50 px-3 py-2.5">
              <span className="flex size-7 shrink-0 items-center justify-center rounded-lg bg-blue-100 text-blue-700">
                <Users className="size-4" />
              </span>
              <div className="flex flex-col gap-0.5 min-w-0">
                <span className="text-[10px] font-semibold uppercase tracking-wide text-blue-700">
                  Se agruparán mesas
                </span>
                <span className="text-xs text-blue-900 leading-snug">
                  {[table.name, ...combineNames].join(" + ")}
                </span>
              </div>
            </div>
          )}
          {upcomingClashes.length > 0 && (
            <div className="mt-1 flex items-start gap-2.5 rounded-xl border border-amber-300 bg-amber-50 px-3 py-2.5">
              <span className="flex size-7 shrink-0 items-center justify-center rounded-lg bg-amber-100 text-amber-700">
                <Clock className="size-4" />
              </span>
              <div className="flex flex-col gap-1 min-w-0 flex-1">
                <span className="text-[10px] font-semibold uppercase tracking-wide text-amber-800">
                  Posible sobrereserva
                </span>
                <span className="text-xs text-amber-900 leading-snug">
                  {upcomingClashes.length === 1
                    ? "Hay una reserva próxima sobre esta(s) mesa(s):"
                    : `Hay ${upcomingClashes.length} reservas próximas sobre esta(s) mesa(s):`}
                </span>
                <ul className="flex flex-col gap-0.5 text-xs text-amber-900">
                  {upcomingClashes.map(({ reservation, startsAtLocal }) => {
                    const minutesAway = Math.round(startsAtLocal.diff(dayjs(), "minute", true));
                    const tableNames = reservation.tableIds
                      .map((tid) => storeTables.find((t) => t.id === tid)?.name ?? "?")
                      .join(" + ");
                    return (
                      <li key={reservation.id} className="leading-snug">
                        <strong>{reservation.time}</strong>
                        {" · "}
                        {reservation.guestName || "Sin nombre"} ({reservation.pax}p) · {tableNames}
                        <span className="ml-1 text-amber-700">— en {minutesAway} min</span>
                      </li>
                    );
                  })}
                </ul>
                {cappedEndTime && (
                  <span className="text-[11px] text-amber-900">
                    Al confirmar, el walk-in se sentará hasta las <strong>{cappedEndTime}</strong>{" "}
                    para liberar la mesa a tiempo.
                  </span>
                )}
              </div>
            </div>
          )}
        </div>

        <div className="flex flex-col gap-1.5">
          <Label className="text-xs text-gray-500 font-medium uppercase tracking-wide">
            Nombre o búsqueda
          </Label>
          <Input
            type="text"
            value={walkInName}
            onChange={(e) => {
              setWalkInName(e.target.value);
              // Typing again after picking unlinks — operator clearly
              // wants to override the matched guest.
              if (walkInGuestId) {
                setWalkInGuestId(null);
              }
            }}
            placeholder="Nombre, teléfono o email"
            className="h-11 text-base px-3.5"
          />
          {visibleMatches.length > 0 && (
            <div className="flex flex-col gap-0.5 rounded-xl border border-input bg-white p-1 shadow-sm">
              {visibleMatches.map((g) => (
                <button
                  key={g.id}
                  type="button"
                  className="flex flex-col gap-0.5 rounded-lg px-2.5 py-1.5 text-left text-xs hover:bg-muted"
                  onClick={() => {
                    setWalkInGuestId(g.id);
                    setWalkInName(g.name);
                    setWalkInPhone(g.phone || "");
                    setWalkInEmail(g.email || "");
                  }}
                >
                  <span className="font-semibold text-foreground">{g.name}</span>
                  <span className="text-[10px] text-text-muted">
                    {[g.phone, g.email].filter(Boolean).join(" · ") || "Sin contacto"}
                  </span>
                </button>
              ))}
            </div>
          )}
          {walkInGuestId && (
            <p className="text-[10px] text-green-700">
              ✓ Comensal vinculado — al guardar se actualiza su historial.
            </p>
          )}
        </div>

        <div className="grid grid-cols-2 gap-2">
          <div className="flex flex-col gap-1.5">
            <Label className="text-xs text-gray-500 font-medium uppercase tracking-wide">
              Teléfono <span className="font-normal normal-case text-text-muted">· opcional</span>
            </Label>
            <Input
              type="tel"
              inputMode="tel"
              value={walkInPhone}
              onChange={(e) => setWalkInPhone(e.target.value)}
              placeholder="+57…"
              className="h-11 text-base px-3.5"
            />
          </div>
          <div className="flex flex-col gap-1.5">
            <Label className="text-xs text-gray-500 font-medium uppercase tracking-wide">
              Email <span className="font-normal normal-case text-text-muted">· opcional</span>
            </Label>
            <Input
              type="email"
              inputMode="email"
              value={walkInEmail}
              onChange={(e) => setWalkInEmail(e.target.value)}
              placeholder="nombre@correo.com"
              className="h-11 text-base px-3.5"
            />
          </div>
        </div>
      </div>

      <div className="mt-5 flex items-stretch gap-2">
        <Button variant="outline" className="flex-1 h-11 text-base" onClick={onClose}>
          Cancelar
        </Button>
        <Button className="flex-1 h-11 text-base" onClick={handleSubmit} disabled={planIsStale}>
          {planIsStale ? "Calculando…" : "Sentar walk-in"}
        </Button>
      </div>

      <WalkinOverbookConfirm
        open={showOverbookConfirm}
        clashes={upcomingClashes}
        cappedEndTime={cappedEndTime}
        tableNameOf={(id) => storeTables.find((t) => t.id === id)?.name ?? "?"}
        onCancel={() => setShowOverbookConfirm(false)}
        onConfirm={() => {
          // Always close the modal so a synchronous failure inside
          // ``commitSeat`` doesn't leave the operator stuck on stale
          // clash data. On success the panel unmounts via
          // ``onClose()`` and the close is a no-op.
          try {
            commitSeat();
          } finally {
            setShowOverbookConfirm(false);
          }
        }}
      />
    </div>
  );
}

// ── Detail Panel ──────────────────────────────────────────────

interface DetailPanelProps {
  selectedTableId: string | null;
  setSelectedTableId: (id: string | null) => void;
  storeTables: Table[];
  showReassign: boolean;
  setShowReassign: (v: boolean) => void;
  setDetailReservationId: (id: string | null) => void;
  onClose: () => void;
}

function DetailPanel({
  selectedTableId,
  setSelectedTableId,
  storeTables,
  showReassign,
  setShowReassign,
  setDetailReservationId,
  onClose,
}: DetailPanelProps) {
  // Re-render every 60 s so the "restante" countdown stays fresh.
  const [, setTick] = useState(0);
  useEffect(() => {
    const id = setInterval(() => setTick((t) => t + 1), 60_000);
    return () => clearInterval(id);
  }, []);
  const { data: tableGroups } = useTableGroups();
  const { data: zones } = useZones();
  const [explorerMode, setExplorerMode] = useState<
    null | "reassign" | "seat-override" | "mid-service"
  >(null);
  const locationTz = useActiveLocationTimezone();

  const table = storeTables.find((t) => t.id === selectedTableId);
  const reservation = selectedTableId !== null ? getTableReservation(selectedTableId) : undefined;
  if (!table || !reservation) {
    return null;
  }

  const statusBadge: Record<string, { label: string; cls: string }> = {
    noshow: { cls: "bg-red-100 text-red-700", label: "No Show" },
    reservada: { cls: "bg-yellow-100 text-yellow-700", label: "Activa" },
    sentado: { cls: "bg-status-seated text-white", label: "Sentado" },
  };
  const badge = statusBadge[reservation.status] || statusBadge.reservada;
  const initials = reservation.guestName
    .split(" ")
    .map((w) => w[0])
    .join("")
    .slice(0, 2);

  const openFullDetail = () => {
    setDetailReservationId(reservation.id);
    onClose();
  };

  const handleSeat = () => {
    markSeated(reservation.id);
    onClose();
  };
  const handleComplete = () => {
    markCompleted(reservation.id);
    onClose();
  };
  const handleNoShow = () => {
    markNoShow(reservation.id);
    onClose();
  };
  const handleRevertNoShow = () => {
    const result = revertNoShow(reservation.id);
    if (result === "seated") {
      onClose();
    } else if (result === "table-taken") {
      setDetailReservationId(reservation.id);
      onClose();
    }
  };
  const handleCancel = () => {
    cancelReservation(reservation.id, "Cancelado por staff");
    onClose();
  };

  // Honor a registered ``TableGroup`` whose tables exactly match the
  // reservation's set: its ``max_capacity`` is the operator's declared truth
  // for that joining and beats per-table sums (e.g. "joining 4+5 fits 8 even
  // though sum is 6").
  const matchingGroupForReservation = tableGroups?.find(
    (g) =>
      g.tables.length === reservation.tableIds.length &&
      reservation.tableIds.every((id) => g.tables.includes(id)),
  );
  const sumCapacity = reservation.tableIds.reduce((sum, id) => {
    const tbl = storeTables.find((t) => t.id === id);
    return sum + (tbl?.maxCapacity || 0);
  }, 0);
  const currentCapacity = matchingGroupForReservation?.max_capacity ?? sumCapacity;
  const capacityShort = reservation.pax > currentCapacity;
  const suggestStartsAt = dayjs(
    `${reservation.date} ${reservation.time}`,
    "YYYY-MM-DD h:mm A",
  ).toISOString();
  const suggestEndsAt = dayjs(`${reservation.date} ${reservation.time}`, "YYYY-MM-DD h:mm A")
    .add(3, "hour")
    .toISOString();
  const { data: suggestions = [] } = useSuggestTables({
    pax: reservation.pax,
    startsAt: suggestStartsAt,
    endsAt: suggestEndsAt,
    enabled: showReassign,
  });

  const handleReassign = (tableIds: string[]) => {
    void reassignTable(reservation.id, tableIds).then((ok) => {
      if (!ok) return;
      setFocusTableIdsAction(tableIds);
      setShowReassign(false);
    });
  };

  // Reassign view
  if (showReassign) {
    return (
      <div className="bg-white rounded-xl shadow-xl border border-border p-4 flex flex-col gap-2.5">
        <div className="flex items-center justify-between">
          <h3 className="text-sm font-bold">Reasignar mesa</h3>
          <Button variant="ghost" size="xs" onClick={() => setShowReassign(false)}>
            Volver
          </Button>
        </div>
        <p className="text-[11px] text-text-secondary">
          {reservation.guestName} · {reservation.pax} personas · Actual:{" "}
          {reservation.tableIds
            .map((id) => storeTables.find((t) => t.id === id)?.name || "Mesa")
            .join(" + ")}
        </p>
        {suggestions.length === 0 ? (
          <div className="text-[11px] text-amber-700 bg-amber-50 rounded px-2.5 py-2">
            No hay mesas disponibles para {reservation.pax} personas a las {reservation.time}
          </div>
        ) : (
          <div className="flex flex-col gap-1.5 max-h-50 overflow-y-auto">
            {suggestions.slice(0, 8).map((s) => {
              const label = s.tableIds
                .map((id) => storeTables.find((t) => t.id === id)?.name || "Mesa")
                .join(" + ");
              const isGroup = s.tableIds.length > 1;
              return (
                <Button
                  key={s.tableIds.join(",")}
                  variant="outline"
                  className="h-auto flex items-center justify-between px-2.5 py-2 rounded-lg hover:bg-blue-50 hover:border-blue-300 text-left transition-colors"
                  onClick={() => handleReassign(s.tableIds)}
                >
                  <div>
                    <span className="text-[11px] font-medium">{label}</span>
                    {isGroup && (
                      <span className="text-[10px] text-blue-600 ml-1.5">agrupación</span>
                    )}
                  </div>
                  <div className="text-[10px] text-text-secondary">
                    {s.totalCapacity}p
                    {s.wastedCapacity > 0 && (
                      <span className="text-text-muted"> (+{s.wastedCapacity})</span>
                    )}
                  </div>
                </Button>
              );
            })}
          </div>
        )}
        <Button
          variant="outline"
          className="w-full gap-1.5 text-blue-600 border-blue-200 hover:bg-blue-50"
          size="xs"
          onClick={() => {
            useAppStore.getState().setMapReassignMode({
              reservationId: reservation.id,
              pax: reservation.pax,
            });
            setShowReassign(false);
            onClose();
          }}
        >
          <MapPin size={12} />
          Seleccionar en mapa
        </Button>
      </div>
    );
  }

  return (
    <div
      className="bg-white p-4 rounded-2xl w-80 text-sm overflow-y-auto"
      style={{
        boxShadow: "0 8px 28px rgba(0,0,0,0.14)",
        maxHeight: "calc(100dvh - 80px)",
      }}
    >
      {/* Header */}
      <div className="flex justify-between items-start mb-4 gap-3">
        <div className="flex min-w-0 flex-1 items-center gap-3">
          <span className="flex size-11 shrink-0 items-center justify-center rounded-xl bg-orange-500 text-white text-base font-bold">
            {initials}
          </span>
          <div className="min-w-0 flex-1">
            <h3 className="text-lg font-semibold text-gray-900 truncate leading-tight">
              {reservation.guestName}
            </h3>
            <p className="text-xs text-text-muted truncate">
              {reservation.guestEmail || reservation.guestPhone || `Mesa ${table.name}`}
            </p>
          </div>
        </div>
        <div className="flex shrink-0 items-center gap-1">
          <ReservationContextMenu reservation={reservation} onOpenExplorer={setExplorerMode} />
          <button
            type="button"
            aria-label="Cerrar"
            className="flex size-9 shrink-0 items-center justify-center rounded-full text-gray-500 hover:bg-gray-100 hover:text-gray-800"
            onClick={onClose}
          >
            <X className="size-5" />
          </button>
        </div>
      </div>

      {/* Status + tags */}
      <div className="flex flex-wrap items-center gap-1.5 mb-3">
        <Badge className={`text-[11px] font-semibold px-2.5 py-1 ${badge.cls}`}>
          {badge.label}
        </Badge>
        <PinBadge level={reservation.pinLevel} />
        {reservation.isConfirmedByHost && reservation.status !== "sentado" && (
          <Badge className="text-[11px] px-2.5 py-1 rounded-full bg-green-100 text-green-700 font-medium">
            <Check className="size-3 mr-0.5" /> Confirmado
          </Badge>
        )}
        {reservation.tags.map((tag) => (
          <Badge
            key={tag}
            variant="secondary"
            className="text-[11px] px-2.5 py-1 rounded-full font-medium"
          >
            {reservationTagLabel(tag)}
          </Badge>
        ))}
      </div>

      {/* Info card */}
      <div className="rounded-xl bg-muted/40 px-3 py-2.5 flex flex-col gap-1.5 text-xs">
        <div className="flex items-center justify-between">
          <span className="inline-flex items-center gap-1.5 text-text-secondary">
            <Users className="size-3.5" /> Personas
          </span>
          <span className="font-semibold tabular-nums">{reservation.pax}</span>
        </div>
        <div className="flex items-center justify-between">
          <span className="inline-flex items-center gap-1.5 text-text-secondary">
            <Clock className="size-3.5" /> Reserva
          </span>
          <span className="font-semibold tabular-nums">{reservation.time}</span>
        </div>
        <div className="flex items-center justify-between">
          <span className="inline-flex items-center gap-1.5 text-text-secondary">
            <Clock className="size-3.5" /> Hasta
          </span>
          <span className="font-semibold tabular-nums">
            {reservation.endTime}
            {reservation.status === "sentado" && (
              <span className="ml-1.5 text-[11px] font-normal text-text-muted">
                ({formatRemainingTime(reservation.endTime, reservation.endDate ?? reservation.date)}{" "}
                restante)
              </span>
            )}
          </span>
        </div>
      </div>

      {/* Reassign chip — opens the sidebar's unified edit form
          straight into edit mode (pax / fecha / hora / zona + Cambiar
          mesa via the explorer + live dry-run). Walk-ins skip this:
          they don't have a booked window to edit; the only sensible
          action on them is mid-service relocation via the ⋯ menu. */}
      {reservation.origin !== "walkin" && (
        <button
          type="button"
          className="mt-3 w-full flex items-center justify-between gap-3 rounded-xl border border-blue-200 bg-blue-50 hover:bg-blue-100 px-3 py-2.5 text-left transition-colors"
          onClick={() => {
            useAppStore.getState().setPendingEditReservationId(reservation.id);
            setDetailReservationId(reservation.id);
            useAppStore.getState().setSidebarCollapsed(false);
            useAppStore.getState().setShowSidebar(true);
            onClose();
          }}
        >
          <div className="flex items-start gap-2.5 min-w-0">
            <span className="flex size-7 shrink-0 items-center justify-center rounded-lg bg-blue-100 text-blue-700 mt-0.5">
              <MapPin className="size-4" />
            </span>
            <div className="flex flex-col gap-0.5 min-w-0">
              <span className="text-[10px] font-semibold uppercase tracking-wide text-blue-700">
                Editar reserva
              </span>
              <span className="text-xs font-semibold text-foreground truncate">
                {reservation.tableIds
                  .map((id) => storeTables.find((t) => t.id === id)?.name || "Mesa")
                  .join(" + ")}
              </span>
            </div>
          </div>
          <ChevronRight className="size-4 text-blue-600 shrink-0" />
        </button>
      )}

      {/* Capacity warning */}
      {capacityShort && (
        <div className="mt-3 flex items-start gap-2.5 rounded-xl border border-amber-200 bg-amber-50 px-3 py-2.5">
          <span className="flex size-7 shrink-0 items-center justify-center rounded-lg bg-amber-100 text-amber-700">
            <AlertTriangle className="size-4" />
          </span>
          <div className="flex flex-col gap-0.5 min-w-0 text-xs text-amber-900 leading-snug">
            <span className="font-semibold">
              Capacidad insuficiente ({currentCapacity}p para {reservation.pax})
            </span>
            <button
              type="button"
              className="text-amber-700 underline self-start"
              onClick={() => {
                useAppStore.getState().setPendingEditReservationId(reservation.id);
                setDetailReservationId(reservation.id);
                useAppStore.getState().setSidebarCollapsed(false);
                useAppStore.getState().setShowSidebar(true);
                onClose();
              }}
            >
              Ver opciones
            </button>
          </div>
        </div>
      )}

      {/* Risk warning */}
      {reservation.status !== "sentado" && isGuestRisk(reservation.guestName) && (
        <div className="mt-3 flex items-start gap-2.5 rounded-xl border border-amber-200 bg-amber-50 px-3 py-2.5">
          <span className="flex size-7 shrink-0 items-center justify-center rounded-lg bg-amber-100 text-amber-700">
            <AlertTriangle className="size-4" />
          </span>
          <span className="text-xs text-amber-900">Huésped con historial de riesgo</span>
        </div>
      )}

      {/* Notes */}
      {reservation.notes && (
        <div className="mt-3 rounded-xl bg-gray-50 px-3 py-2.5">
          <p className="text-[10px] font-semibold uppercase tracking-wide text-text-secondary">
            Nota
          </p>
          <p className="text-xs text-foreground leading-snug mt-0.5">{reservation.notes}</p>
        </div>
      )}

      {/* Actions */}
      <div className="mt-5 flex flex-col gap-2">
        {reservation.status === "reservada" && (
          <>
            <Button
              className="w-full h-11 text-base bg-green-600 hover:bg-green-700"
              onClick={handleSeat}
            >
              Marcar como sentado
            </Button>
            <div className="flex gap-2">
              <Button
                variant="destructive"
                className="flex-1 h-11 text-base"
                onClick={handleNoShow}
              >
                No-show
              </Button>
              <Button variant="outline" className="flex-1 h-11 text-base" onClick={handleCancel}>
                Cancelar
              </Button>
            </div>
          </>
        )}
        {reservation.status === "sentado" && (
          <Button className="w-full h-11 text-base" onClick={handleComplete}>
            Marcar como completado
          </Button>
        )}
        {reservation.status === "noshow" && (
          <Button
            className="w-full h-11 text-base bg-amber-500 hover:bg-amber-600"
            onClick={handleRevertNoShow}
          >
            Revertir no-show
          </Button>
        )}
        <Button
          variant="outline"
          className="w-full h-11 text-base flex items-center justify-center gap-1"
          onClick={openFullDetail}
        >
          Ver detalle completo
          <ChevronRight className="size-4" />
        </Button>
      </div>

      {/* TableExplorer drawer — invoked from the context menu's
          "Cambiar mesa…" / "Mover de mesa…" entries. Applies the pick
          via /explore/apply/ (BOOKED) or apiMoveSeated (SEATED). */}
      {explorerMode !== null && (
        <TableExplorer
          open
          onOpenChange={(o) => !o && setExplorerMode(null)}
          context={explorerMode}
          pax={reservation.pax}
          // For ``seat-override`` the party is here NOW so we look for
          // mesas free from this moment; otherwise we use the booked
          // window. ``reservation.time`` / ``endTime`` are formatted
          // strings — parseInTz keeps them anchored to the active TZ.
          startsAt={
            explorerMode === "seat-override"
              ? dayjs().toISOString()
              : parseInTz(
                  `${reservation.date} ${reservation.time}`,
                  "YYYY-MM-DD h:mm A",
                  locationTz,
                ).toISOString()
          }
          endsAt={parseInTz(
            `${reservation.endDate ?? reservation.date} ${reservation.endTime}`,
            "YYYY-MM-DD h:mm A",
            locationTz,
          ).toISOString()}
          preferredZoneId={reservation.preferredZone || null}
          features={(reservation.features as string[]) ?? []}
          excludeReservationId={reservation.id}
          currentTableIds={reservation.tableIds}
          zoneName={(id) => zones?.find((z) => z.id === id)?.name ?? "Zona"}
          onPick={async (pick: TableExplorerPick) => {
            // Seating someone happens with them at the door — a walk-in in
            // all but name. Only a booking placed by hand asks first.
            if (
              explorerMode !== "seat-override" &&
              explorerMode !== "mid-service" &&
              reservation.origin !== "walkin"
            ) {
              const { tables: allTables, zones: allZones } = useAppStore.getState();
              if (!(await confirmReservationTables(pick.tableIds, allTables, allZones))) return;
            }
            // Gate every flow that ships ``requires_moves`` behind an
            // explicit confirm dialog so the operator sees which
            // reservations would relocate before committing.
            if (pick.moves.length > 0) {
              const { useMoveConfirmStore } = await import("@/store/move-confirm-store");
              const intent: "edit" | "seat" =
                explorerMode === "seat-override" || explorerMode === "mid-service"
                  ? "seat"
                  : "edit";
              const allTables = useAppStore.getState().tables;
              const labelIds = (ids: string[] | undefined) =>
                (ids ?? []).map((id) => allTables.find((t) => t.id === id)?.name || "Mesa");
              const confirmed = await useMoveConfirmStore.getState().open({
                intent,
                moves: pick.moves.map((m) => ({
                  reservation_id: m.reservation_id,
                  guest_name: m.guestName || "Otra reserva",
                  pax: m.pax ?? 0,
                  from_table_names: labelIds(m.fromTableIds),
                  to_table_names: labelIds(m.tables),
                })),
              });
              if (!confirmed) return;
            }
            try {
              if (explorerMode === "mid-service") {
                await performMidServiceMove(reservation, pick.tableIds, pick.moves);
              } else if (explorerMode === "seat-override") {
                // Atomic: applies any moves to free the target mesa,
                // assigns the new tables, and transitions to SEATED in
                // a single backend transaction.
                await apiSeatReservation(reservation.id, {
                  tables: pick.tableIds,
                  moves: pick.moves,
                });
              } else {
                // Backend ``TableMoveSchema`` is ``extra='forbid'``;
                // strip the explorer's display-only fields before
                // posting.
                await apiApplyPlan({
                  moves: pick.moves.map((m) => ({
                    reservation_id: m.reservation_id,
                    tables: m.tables,
                  })),
                  target_reservation_id: reservation.id,
                  target_tables: pick.tableIds,
                });
              }
              // Optimistically reflect the move locally so the canvas
              // updates without waiting for the refetch round-trip:
              // patch the reservation's tableIds in the store. The
              // refetch will reconcile shortly after.
              useAppStore.setState((state) => ({
                reservations: state.reservations.map((res) =>
                  res.id === reservation.id ? { ...res, tableIds: pick.tableIds } : res,
                ),
              }));
              // Move the selection + highlight to the new mesa so the
              // detail panel follows the reservation and the floor
              // visualisation reflects the new occupancy immediately.
              setSelectedTableId(pick.tableIds[0] ?? null);
              useAppStore.getState().setHighlightedTableIds(pick.tableIds);
              setFocusTableIdsAction(pick.tableIds);
              setExplorerMode(null);
            } catch (e) {
              console.error("explorer pick failed", e);
            }
          }}
        />
      )}
    </div>
  );
}
